import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowsRotate,
  faDatabase,
  faDownload,
  faEye,
  faFileCsv,
  faFileExcel,
  faFilter,
  faSearch,
  faSpinner,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import toast from "react-hot-toast";
import {
  exportExtractClaimsLogs,
  getExtractClaimsLogDetail,
  getExtractClaimsLogsFilterOptions,
  listExtractClaimsLogs,
} from "../../services/extractClaimsLogsApi";

const PER_PAGE_OPTIONS = [10, 20, 50, 100, 1000, 5000];

const DEFAULT_FILTERS = {
  search: "",
  payload_search: "",
  run_uid: "",
  sequence_no: "",
  step_key: "",
  status: "",
  performed_by: "",
  user_id: "",
  ip_address: "",
  has_error: "",
  date_from: "",
  date_to: "",
  sort_by: "id",
  sort_direction: "desc",
};

const SUMMARY_FIELDS = [
  ["id", "ID"],
  ["run_uid", "Run UID"],
  ["sequence_no", "Sequence No"],
  ["step_key", "Step Key"],
  ["step_name", "Step Name"],
  ["status", "Status"],
  ["message", "Message"],
  ["affected_rows", "Affected Rows"],
  ["inserted_rows", "Inserted Rows"],
  ["updated_rows", "Updated Rows"],
  ["selected_rows", "Selected Rows"],
  ["started_at", "Started At"],
  ["finished_at", "Finished At"],
  ["duration_ms", "Duration MS"],
  ["performed_by", "Performed By"],
  ["user_id", "User ID"],
  ["ip_address", "IP Address"],
  ["created_at", "Created At"],
  ["updated_at", "Updated At"],
];

const LONG_TEXT_FIELDS = [
  ["request_payload", "Request Payload"],
  ["result_payload", "Result Payload"],
  ["sql_context", "SQL Context"],
  ["error_message", "Error Message"],
  ["error_trace", "Error Trace"],
  ["user_agent", "User Agent"],
];

const formatDateTime = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString("en-PH");
};

const formatNumber = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const numberValue = Number(value);
  if (Number.isNaN(numberValue)) return String(value);
  return numberValue.toLocaleString("en-PH");
};

const stringifyValue = (value) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return "";
    try {
      return JSON.stringify(JSON.parse(trimmed), null, 2);
    } catch (error) {
      return value;
    }
  }

  try {
    return JSON.stringify(value, null, 2);
  } catch (error) {
    return String(value);
  }
};

const cleanParams = (params) => {
  const cleaned = { ...params };
  Object.keys(cleaned).forEach((key) => {
    if (cleaned[key] === "" || cleaned[key] === null || cleaned[key] === undefined) {
      delete cleaned[key];
    }
  });
  return cleaned;
};

const getPaginationWindow = (currentPage, totalPages) => {
  const maxVisible = 5;
  const pages = [];
  let start = Math.max(1, currentPage - 2);
  let end = Math.min(totalPages, start + maxVisible - 1);

  if (end - start < maxVisible - 1) {
    start = Math.max(1, end - maxVisible + 1);
  }

  for (let page = start; page <= end; page += 1) {
    pages.push(page);
  }

  return pages;
};

const getStatusBadgeClass = (status) => {
  const normalized = String(status || "").toLowerCase();

  if (normalized === "success") {
    return "border-emerald-200 bg-emerald-50 text-emerald-700";
  }

  if (normalized === "failed") {
    return "border-red-200 bg-red-50 text-red-700";
  }

  if (normalized === "started") {
    return "border-blue-200 bg-blue-50 text-blue-700";
  }

  if (normalized === "skipped") {
    return "border-amber-200 bg-amber-50 text-amber-700";
  }

  return "border-gray-200 bg-gray-50 text-gray-700";
};

const StatusBadge = ({ status }) => (
  <span
    className={`inline-flex max-w-full rounded-full border px-2 py-0.5 text-[11px] font-semibold ${getStatusBadgeClass(
      status
    )}`}
  >
    <span className="truncate">{status || "-"}</span>
  </span>
);

const TextPreview = ({ value, length }) => {
  const text = stringifyValue(value);
  const totalLength = Number(length || text.length || 0);

  return (
    <div className="space-y-1">
      <textarea
        readOnly
        value={text}
        className="h-24 w-[340px] resize-y rounded-md border border-gray-200 bg-gray-50 px-2 py-1.5 font-mono text-[11px] leading-4 text-gray-700 outline-none"
      />
      {totalLength > text.length && (
        <div className="text-[10px] text-gray-500">
          Preview {text.length.toLocaleString("en-PH")} of {totalLength.toLocaleString("en-PH")} chars
        </div>
      )}
    </div>
  );
};

const ExtractClaimsLogsPage = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState("");
  const [error, setError] = useState("");
  const [filterForm, setFilterForm] = useState(DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS);
  const [filterOptions, setFilterOptions] = useState({
    step_keys: [],
    statuses: [],
    performed_by: [],
    summary: {},
  });
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchFilterOptions = useCallback(async () => {
    try {
      const response = await getExtractClaimsLogsFilterOptions();
      setFilterOptions(response.data?.data || {});
    } catch (requestError) {
      setError(
        requestError?.response?.data?.message ||
          "Failed to load Extract Claims log filter options."
      );
    }
  }, []);

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const response = await listExtractClaimsLogs(
        cleanParams({
          page,
          per_page: perPage,
          ...appliedFilters,
        })
      );
      const payload = response.data || {};
      const pagination = payload.pagination || {};

      setRows(Array.isArray(payload.data) ? payload.data : []);
      setTotalRecords(Number(pagination.total_records || 0));
      setTotalPages(Math.max(1, Number(pagination.total_pages || 1)));
    } catch (requestError) {
      setRows([]);
      setTotalRecords(0);
      setTotalPages(1);
      setError(
        requestError?.response?.data?.message ||
          "Failed to load Extract Claims logs."
      );
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, page, perPage]);

  useEffect(() => {
    fetchFilterOptions();
  }, [fetchFilterOptions]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const showingText = useMemo(() => {
    if (!totalRecords) return "No records";
    const from = (page - 1) * perPage + 1;
    const to = Math.min(page * perPage, totalRecords);
    return `Showing ${from.toLocaleString("en-PH")} to ${to.toLocaleString("en-PH")} of ${totalRecords.toLocaleString("en-PH")}`;
  }, [page, perPage, totalRecords]);

  const paginationWindow = useMemo(
    () => getPaginationWindow(page, totalPages),
    [page, totalPages]
  );

  const updateFilter = (key, value) => {
    setFilterForm((previous) => ({
      ...previous,
      [key]: value,
    }));
  };

  const applyFilters = (event) => {
    event.preventDefault();
    setPage(1);
    setAppliedFilters({ ...filterForm });
  };

  const resetFilters = () => {
    setFilterForm(DEFAULT_FILTERS);
    setAppliedFilters(DEFAULT_FILTERS);
    setPage(1);
    setPerPage(20);
  };

  const handleSort = (sortByKey) => {
    setPage(1);
    setFilterForm((previous) => {
      const nextDirection =
        previous.sort_by === sortByKey && previous.sort_direction === "asc"
          ? "desc"
          : "asc";
      const nextForm = {
        ...previous,
        sort_by: sortByKey,
        sort_direction: nextDirection,
      };
      setAppliedFilters(nextForm);
      return nextForm;
    });
  };

  const sortLabel = (key) => {
    if (appliedFilters.sort_by !== key) return "";
    return appliedFilters.sort_direction === "asc" ? " ASC" : " DESC";
  };

  const renderSortButton = (key, label) => (
    <button
      type="button"
      onClick={() => handleSort(key)}
      className="font-semibold text-gray-700 hover:text-blue-700"
    >
      {label}
      <span className="ml-1 text-[10px] text-gray-400">{sortLabel(key)}</span>
    </button>
  );

  const openDetail = async (row) => {
    if (!row?.id) return;

    setDetailOpen(true);
    setDetailLoading(true);
    setSelectedDetail(null);

    try {
      const response = await getExtractClaimsLogDetail(row.id);
      setSelectedDetail(response.data?.data || null);
    } catch (requestError) {
      toast.error(
        requestError?.response?.data?.message ||
          "Failed to load Extract Claims log detail."
      );
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setDetailOpen(false);
    setSelectedDetail(null);
  };

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const exportRows = async (format) => {
    try {
      setExporting(format);
      const response = await exportExtractClaimsLogs(
        cleanParams({
          ...appliedFilters,
          format,
          export_limit: 50000,
        })
      );
      const extension = format === "excel" ? "xls" : "csv";
      const timestamp = new Date()
        .toISOString()
        .replace(/[-:T]/g, "")
        .slice(0, 14);
      downloadBlob(response.data, `extract_claims_logs_${timestamp}.${extension}`);
      toast.success(`Exported Extract Claims logs as ${format.toUpperCase()}.`);
    } catch (requestError) {
      toast.error(
        requestError?.response?.data?.message ||
          `Failed to export Extract Claims logs as ${format.toUpperCase()}.`
      );
    } finally {
      setExporting("");
    }
  };

  const summary = filterOptions.summary || {};

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="border-b border-gray-200 bg-white px-6 py-5">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-10 w-10 flex-none items-center justify-center rounded-md bg-gray-900 text-white">
              <FontAwesomeIcon icon={faDatabase} />
            </div>
            <div className="min-w-0">
              <h1 className="text-2xl font-semibold text-gray-900">
                Extract Claims Logs
              </h1>
              <div className="mt-1 flex flex-wrap gap-2 text-xs text-gray-600">
                <span>Total {formatNumber(summary.total_logs || 0)}</span>
                <span>Success {formatNumber(summary.success_logs || 0)}</span>
                <span>Failed {formatNumber(summary.failed_logs || 0)}</span>
                <span>Latest {formatDateTime(summary.latest_created_at)}</span>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={fetchLogs}
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-60"
            >
              <FontAwesomeIcon icon={faArrowsRotate} />
              Refresh
            </button>
            <button
              type="button"
              onClick={() => exportRows("csv")}
              disabled={Boolean(exporting)}
              className="inline-flex items-center justify-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-100 disabled:opacity-60"
            >
              <FontAwesomeIcon icon={exporting === "csv" ? faSpinner : faFileCsv} spin={exporting === "csv"} />
              CSV
            </button>
            <button
              type="button"
              onClick={() => exportRows("excel")}
              disabled={Boolean(exporting)}
              className="inline-flex items-center justify-center gap-2 rounded-md border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-100 disabled:opacity-60"
            >
              <FontAwesomeIcon icon={exporting === "excel" ? faSpinner : faFileExcel} spin={exporting === "excel"} />
              Excel
            </button>
          </div>
        </div>
      </div>

      <div className="space-y-4 p-6">
        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {typeof error === "string" ? error : JSON.stringify(error)}
          </div>
        )}

        <form
          onSubmit={applyFilters}
          className="grid grid-cols-1 gap-3 rounded-md border border-gray-200 bg-white p-4 md:grid-cols-6"
        >
          <div className="relative md:col-span-2">
            <FontAwesomeIcon
              icon={faSearch}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              value={filterForm.search}
              onChange={(event) => updateFilter("search", event.target.value)}
              placeholder="Search ID, run UID, step, message, user..."
              className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
            />
          </div>

          <div className="relative md:col-span-2">
            <FontAwesomeIcon
              icon={faSearch}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              value={filterForm.payload_search}
              onChange={(event) => updateFilter("payload_search", event.target.value)}
              placeholder="Payload search"
              className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
            />
          </div>

          <input
            type="text"
            value={filterForm.run_uid}
            onChange={(event) => updateFilter("run_uid", event.target.value)}
            placeholder="Run UID"
            className="rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
          />

          <select
            value={filterForm.sequence_no}
            onChange={(event) => updateFilter("sequence_no", event.target.value)}
            className="rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
          >
            <option value="">All Sequence</option>
            <option value="0">0 - Run</option>
            <option value="1">1 - Provider/Doctor</option>
            <option value="2">2 - Sequence No</option>
            <option value="3">3 - Check PRN</option>
            <option value="4">4 - Settlement</option>
          </select>

          <select
            value={filterForm.step_key}
            onChange={(event) => updateFilter("step_key", event.target.value)}
            className="rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
          >
            <option value="">All Steps</option>
            {(filterOptions.step_keys || []).map((stepKey) => (
              <option key={stepKey} value={stepKey}>
                {stepKey}
              </option>
            ))}
          </select>

          <select
            value={filterForm.status}
            onChange={(event) => updateFilter("status", event.target.value)}
            className="rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
          >
            <option value="">All Status</option>
            {(filterOptions.statuses || []).map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>

          <select
            value={filterForm.has_error}
            onChange={(event) => updateFilter("has_error", event.target.value)}
            className="rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
          >
            <option value="">All Error State</option>
            <option value="1">With Error</option>
            <option value="0">No Error</option>
          </select>

          <input
            type="text"
            value={filterForm.performed_by}
            onChange={(event) => updateFilter("performed_by", event.target.value)}
            placeholder="Performed By"
            className="rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
          />

          <input
            type="number"
            min="1"
            value={filterForm.user_id}
            onChange={(event) => updateFilter("user_id", event.target.value)}
            placeholder="User ID"
            className="rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
          />

          <input
            type="text"
            value={filterForm.ip_address}
            onChange={(event) => updateFilter("ip_address", event.target.value)}
            placeholder="IP Address"
            className="rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
          />

          <input
            type="date"
            value={filterForm.date_from}
            onChange={(event) => updateFilter("date_from", event.target.value)}
            className="rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
          />

          <input
            type="date"
            value={filterForm.date_to}
            onChange={(event) => updateFilter("date_to", event.target.value)}
            className="rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
          />

          <div className="flex flex-wrap items-center gap-2 md:col-span-6">
            <button
              type="submit"
              className="inline-flex items-center gap-2 rounded-md bg-gray-900 px-4 py-2 text-sm font-semibold text-white hover:bg-gray-800"
            >
              <FontAwesomeIcon icon={faFilter} />
              Apply Filters
            </button>
            <button
              type="button"
              onClick={resetFilters}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
            >
              Reset
            </button>
          </div>
        </form>

        <div className="overflow-hidden rounded-md border border-gray-200 bg-white">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 px-4 py-3 text-sm text-gray-600">
            <span>{showingText}</span>
            <div className="flex items-center gap-2">
              <span>Display per page</span>
              <select
                value={perPage}
                onChange={(event) => {
                  setPerPage(Number(event.target.value));
                  setPage(1);
                }}
                className="rounded-md border border-gray-300 px-2 py-1"
              >
                {PER_PAGE_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="overflow-auto">
            <table className="min-w-[2600px] text-sm">
              <thead className="bg-gray-50 text-gray-700">
                <tr>
                  <th className="px-3 py-3 text-left">{renderSortButton("id", "id")}</th>
                  <th className="px-3 py-3 text-left">{renderSortButton("created_at", "created_at")}</th>
                  <th className="px-3 py-3 text-left">{renderSortButton("run_uid", "run_uid")}</th>
                  <th className="px-3 py-3 text-left">{renderSortButton("sequence_no", "seq")}</th>
                  <th className="px-3 py-3 text-left">{renderSortButton("step_key", "step")}</th>
                  <th className="px-3 py-3 text-left">{renderSortButton("status", "status")}</th>
                  <th className="px-3 py-3 text-left">message</th>
                  <th className="px-3 py-3 text-left">{renderSortButton("selected_rows", "selected")}</th>
                  <th className="px-3 py-3 text-left">{renderSortButton("inserted_rows", "inserted")}</th>
                  <th className="px-3 py-3 text-left">{renderSortButton("updated_rows", "updated")}</th>
                  <th className="px-3 py-3 text-left">{renderSortButton("affected_rows", "affected")}</th>
                  <th className="px-3 py-3 text-left">{renderSortButton("duration_ms", "duration")}</th>
                  <th className="px-3 py-3 text-left">request_payload</th>
                  <th className="px-3 py-3 text-left">result_payload</th>
                  <th className="px-3 py-3 text-left">sql_context</th>
                  <th className="px-3 py-3 text-left">error_message</th>
                  <th className="px-3 py-3 text-left">{renderSortButton("performed_by", "performed_by")}</th>
                  <th className="px-3 py-3 text-left">{renderSortButton("user_id", "user_id")}</th>
                  <th className="px-3 py-3 text-left">ip_address</th>
                  <th className="sticky right-0 bg-gray-50 px-3 py-3 text-left">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr>
                    <td colSpan={20} className="px-4 py-10 text-center text-gray-500">
                      <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                      Loading Extract Claims logs...
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={20} className="px-4 py-10 text-center text-gray-500">
                      No Extract Claims logs found.
                    </td>
                  </tr>
                ) : (
                  rows.map((row) => (
                    <tr key={row.id} className="hover:bg-blue-50/30">
                      <td className="px-3 py-3 font-mono text-xs">{row.id}</td>
                      <td className="px-3 py-3">{formatDateTime(row.created_at)}</td>
                      <td className="px-3 py-3">
                        <div className="max-w-[250px] break-all font-mono text-xs text-gray-800">
                          {row.run_uid || "-"}
                        </div>
                      </td>
                      <td className="px-3 py-3 font-mono text-xs">{row.sequence_no}</td>
                      <td className="px-3 py-3">
                        <div className="max-w-[230px] space-y-1">
                          <div className="break-words font-mono text-xs text-gray-900">
                            {row.step_key || "-"}
                          </div>
                          <div className="text-xs text-gray-500">{row.step_name || "-"}</div>
                        </div>
                      </td>
                      <td className="px-3 py-3"><StatusBadge status={row.status} /></td>
                      <td className="px-3 py-3">
                        <div className="max-w-[260px] break-words text-sm text-gray-700">
                          {row.message || "-"}
                        </div>
                      </td>
                      <td className="px-3 py-3 text-right font-mono text-xs">{formatNumber(row.selected_rows)}</td>
                      <td className="px-3 py-3 text-right font-mono text-xs">{formatNumber(row.inserted_rows)}</td>
                      <td className="px-3 py-3 text-right font-mono text-xs">{formatNumber(row.updated_rows)}</td>
                      <td className="px-3 py-3 text-right font-mono text-xs">{formatNumber(row.affected_rows)}</td>
                      <td className="px-3 py-3 text-right font-mono text-xs">{formatNumber(row.duration_ms)}</td>
                      <td className="px-3 py-3">
                        <TextPreview value={row.request_payload_preview} length={row.request_payload_length} />
                      </td>
                      <td className="px-3 py-3">
                        <TextPreview value={row.result_payload_preview} length={row.result_payload_length} />
                      </td>
                      <td className="px-3 py-3">
                        <TextPreview value={row.sql_context_preview} length={row.sql_context_length} />
                      </td>
                      <td className="px-3 py-3">
                        <TextPreview value={row.error_message} length={row.error_message?.length || 0} />
                        {row.has_error_trace && (
                          <div className="mt-1 text-[10px] font-semibold text-red-600">
                            error_trace {formatNumber(row.error_trace_length)} chars
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-3">{row.performed_by || "-"}</td>
                      <td className="px-3 py-3 font-mono text-xs">{row.user_id ?? "-"}</td>
                      <td className="px-3 py-3 font-mono text-xs">{row.ip_address || "-"}</td>
                      <td className="sticky right-0 bg-white px-3 py-3">
                        <button
                          type="button"
                          onClick={() => openDetail(row)}
                          className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                        >
                          <FontAwesomeIcon icon={faEye} />
                          View
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-200 px-4 py-3">
            <div className="text-sm text-gray-600">
              Page {page.toLocaleString("en-PH")} of {totalPages.toLocaleString("en-PH")}
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setPage((previous) => Math.max(1, previous - 1))}
                disabled={page <= 1}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-40"
              >
                Prev
              </button>

              {paginationWindow.map((windowPage) => (
                <button
                  key={windowPage}
                  type="button"
                  onClick={() => setPage(windowPage)}
                  className={`rounded-md px-3 py-1.5 text-xs font-semibold ${
                    windowPage === page
                      ? "bg-gray-900 text-white"
                      : "border border-gray-300 text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  {windowPage}
                </button>
              ))}

              <button
                type="button"
                onClick={() => setPage((previous) => Math.min(totalPages, previous + 1))}
                disabled={page >= totalPages}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>

      {detailOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/45 p-4">
          <div className="max-h-[92vh] w-full max-w-7xl overflow-hidden rounded-md border border-gray-200 bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3">
              <div className="min-w-0">
                <h3 className="text-lg font-semibold text-gray-900">
                  Extract Claims Log Detail
                </h3>
                <p className="mt-0.5 break-all font-mono text-xs text-gray-500">
                  {selectedDetail?.run_uid || "Loading..."}
                </p>
              </div>
              <button
                type="button"
                onClick={closeDetail}
                className="rounded-md border border-gray-300 px-2.5 py-1.5 text-gray-500 hover:bg-gray-50"
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>

            <div className="max-h-[78vh] space-y-5 overflow-auto p-5">
              {detailLoading ? (
                <div className="py-14 text-center text-gray-500">
                  <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                  Loading detail...
                </div>
              ) : !selectedDetail ? (
                <div className="py-14 text-center text-gray-500">
                  No detail available.
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
                    {SUMMARY_FIELDS.map(([key, label]) => (
                      <div key={key} className="rounded-md border border-gray-200 bg-gray-50 p-3">
                        <p className="text-xs font-semibold text-gray-500">{label}</p>
                        <p className="mt-1 break-words text-sm text-gray-900">
                          {["created_at", "updated_at", "started_at", "finished_at"].includes(key)
                            ? formatDateTime(selectedDetail[key])
                            : selectedDetail[key] ?? "-"}
                        </p>
                      </div>
                    ))}
                  </div>

                  <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                    {LONG_TEXT_FIELDS.map(([key, label]) => (
                      <div key={key} className="rounded-md border border-gray-200 p-4">
                        <div className="mb-2 flex items-center justify-between gap-2">
                          <h4 className="text-sm font-semibold text-gray-900">{label}</h4>
                          {key !== "user_agent" && (
                            <FontAwesomeIcon icon={faDownload} className="text-xs text-gray-400" />
                          )}
                        </div>
                        <textarea
                          readOnly
                          value={stringifyValue(selectedDetail[key])}
                          className={`w-full resize-y rounded-md border border-gray-200 bg-gray-50 p-3 font-mono text-xs leading-5 text-gray-800 outline-none ${
                            key === "error_trace" ? "h-80" : "h-56"
                          }`}
                        />
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExtractClaimsLogsPage;

